import { Location } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    CanDeactivate,
    NavigationEnd,
    Params,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { getScrollParent } from '@cmps/cdk';
import { NgbDateAdapter, NgbDateStruct, NgbModal } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { subDays } from 'date-fns';
import {
    animationFrameScheduler,
    combineLatest,
    from,
    Observable,
    of,
    timer,
} from 'rxjs';
import {
    auditTime,
    catchError,
    filter,
    finalize,
    map,
    pairwise,
    retryWhen,
    scan,
    startWith,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { ValidateDialogData } from '../../auth/validate/validate-dialog-data.auth.model';
import { ValidateAuthComponent } from '../../auth/validate/validate.auth.component';
import { ChatContext } from '../../coaching-base/chat-context.model';
import { DispositionReason } from '../../coaching-base/config';
import { CoachingDiffDecoratorService } from '../../coaching-base/diff-decorator';
import {
    ApprovalItem,
    CoachingEventStatus,
    FocusDto,
    NoteCategory,
    ObservationSummaryDto,
} from '../../coaching-base/general';
import { MeetingContext } from '../../coaching-base/meeting-context.model';
import { NotificationScenario } from '../../coaching-base/notifications';
import {
    CoachingSwitchboardService,
    SwitchboardCommitmentComm,
} from '../../coaching-base/switchboard';
import {
    composeCommitmentSummaryItemIdentifier,
    TabRefId,
    UserView,
} from '../../coaching-detail';
import {
    navigateToDetail,
    navigateToExistingCommitment,
    navigateToExistingDraftCommitment,
    replaceStateToExistingCommitment,
    replaceStateToExistingDraftCommitment,
} from '../../coaching-detail/navigate';
import { ClickOptions } from '../../coaching-insight/click-options';
import { NoteDetailFormValue } from '../../coaching-note/note-detail-form-value.model';
import {
    BringIntoFocusService,
    scrollIntoViewInstant,
} from '../../coaching-observation/bring-into-focus.service';
import {
    CmpsForm,
    CmpsFormBuilder,
    CmpsFormService,
    cmpsFormServiceFactory,
} from '../../coaching-observation/experimental.form';
import { TagSetTriggerComponent } from '../../coaching-tags/tags/tag-set-trigger.component';
import {
    FocusShallow,
    reconstructTagFromIdentifierNext,
    TagSetMode,
} from '../../coaching-tags/tags/tags.model';
import { FocusSelectorSetTriggerComponent } from '../../coaching/focus-selector/focus-selector-trigger.component';
import { FocusSelectorTriggerPayload } from '../../coaching/focus-selector/model/focus-selector-trigger-payload.model';
import { FocusSelectorTriggerSource } from '../../coaching/focus-selector/model/focus-selector-trigger-source.enum';
import { DateProviders } from '../../common/component/datetime-picker';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import {
    NavigationSrc,
    NavigationSrcQueryParam,
} from '../../notification/navigate';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { MsalService } from '../../teams-call';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { CloseCommitmentComponent } from '../commitment-close/close.commitment.component';
import { DisputeCommitmentComponent } from '../commitment-dispute/dispute.commitment.component';
import { CommitmentCloseGoalValue } from '../commitment-goals/goals.commitment.form';
import { pendingCloseAllGoals } from '../commitment-goals/pending-close-all-goals.function';
import {
    ObservationFormValue,
    observationFormValueTransformer,
} from '../commitment-observations/observations.commitment.form';
import { CommitmentSummaryDialog } from '../commitment-summary/commitment-summary-dialog.component';
import {
    CommitmentSummaryDialogData,
    CommitmentSummaryDialogResult,
    CommitmentSummaryDialogResultAction,
    CommitmentSummaryViewMode,
} from '../commitment-summary/commitment-summary-dialog.model';
import { determineNotificationAddress } from '../determine-notification-address';
import { GoalsCollection } from '../goals-collection.model';

import { CloseContext } from './close-context.model';
import {
    rootClose,
    rootCloseReason,
    rootDelete,
    rootDismissMessage,
    rootDispute,
    rootEdit,
    rootPublish,
    rootReload,
    rootRetrieveObservationSummary,
    rootSave,
    rootSendNotification,
    rootSetTag,
    rootSignOff,
    rootSignOffApproval,
    sendApproval,
} from './commitment.action';
import {
    CommitmentFormFields,
    CommitmentFormValue,
    commitmentFormValueTransformer,
    focusActiveValidator,
    focusChangeValidator,
} from './commitment.form';
import coachingCommitmentReducer from './commitment.reducer';
import { RootState } from './commitment.state';
import { LinkFocusPromiseCallbacks } from './link-focus-promise-callbacks.model';
import { overrideCommitment } from './override-commitment.function';

@Component({
    selector: '[cmpsCommitmentNext]',
    templateUrl: './commitment.component.html',
    host: {
        // TODO revise. change to .cmps-commitment after custom-font.scss->.cmps-commitment:before is refactored out
        // class: 'cmps-commitment',
        // TODO revise. rename css class to a more generic meaning like --grey?
        '[class.cmps-commitment-component--closed]':
            'state?.commitment?.statusId === _CoachingEventStatus.CLOSED || state?.isReadOnly',
        'data-e2e': 'component-commitment',
    },
    providers: [DateProviders],
    viewProviders: [
        BringIntoFocusService,
        { provide: CmpsFormService, useFactory: cmpsFormServiceFactory },
    ],
})
@TakeUntilDestroy()
@UseStore<RootState>(
    'coachingCommitment.root',
    coachingCommitmentReducer.coachingCommitment.root,
)
export class CommitmentComponent
    implements
        OnInit,
        AfterViewInit,
        OnDestroy,
        CanDeactivate<CommitmentComponent>,
        TakeUntilDestroy,
        StateToProps<RootState>,
        SwitchboardCommitmentComm
{
    state: RootState;
    @ViewChild('triggerNext')
    private readonly triggerNext: FocusSelectorSetTriggerComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _TagSetMode = TagSetMode;
    /** @private */ _CoachingEventStatus = CoachingEventStatus;
    /** @private */ _FocusSelectorTriggerSource = FocusSelectorTriggerSource;
    /** @private */ _form: CmpsForm<CommitmentFormFields, CommitmentFormValue>;
    /** @private */ _maxDateStruct: NgbDateStruct;
    /** @private */ _minDateStruct: NgbDateStruct;
    /** @private */ _deliveryTypeOpts: IOption[];
    private _userId: number;
    /** @private */ _selectedUser: UserView;

    /**
     * @private
     * TODO possibly refactor so that only _paramsWatcher/checkParams assigns non-null values to this var.
     * use state for updated values */
    _draftId: string;

    /** @private */ _approvalId: number;

    /**
     * @private
     * TODO possibly refactor so that only _paramsWatcher/checkParams assigns non-null values to this var.
     * use state for updated values */
    _commitmentId: number;

    private _fromOa: boolean;
    private _goalMetricIdList: number[];
    private readonly _closeGoalValueList: CommitmentCloseGoalValue[];

    /** used to set the focus on a new commitment */
    private _focus: FocusShallow;

    /** used to modify the initial form by adding an unsaved linked observation */
    private _observationId: number;

    /**
     * @private
     * bring goals section into view after initial reload
     */
    private _scrollToGoals: boolean;

    /**
     * @private
     * goal to bring into focus after initial reload
     */
    _goalId: number;

    /**
     * @private
     * generic button was clicked in pillar 3 or not
     */
    _isGeneric: boolean;

    /**
     * @private
     * the selected metric. usually from pillar 1
     */
    _activeMetricId: number;

    /**
     * whether the form is initially enabled after initial reload
     */
    private _navigatedWithEditIntent: boolean;

    /**
     * whether the user arrived via a notification
     */
    private _navigatedFromNotification: boolean;
    /**
     * whether the user arrived via a notification
     */
    private _navigatedFromInsight: boolean;

    private _canDeactivateWithoutConfirm: boolean;
    /**
     * @private
     * pseudo dirty state of the form
     */
    _dirty: boolean;

    /**
     * @private
     * visibility holder for header alert since it is dismissible and there is no action for it in the reducer
     */
    _showApprovalAlert: boolean;
    /**
     * @private
     * last linked observations
     */
    _lastLinkedObservation: ObservationSummaryDto;

    private _modalRefs: {
        closeReason?: CmpsDialogRef<CloseCommitmentComponent, any>;
        close?: CmpsDialogRef<CmpsConfirmDialogComponent, any>;
        dispute?: CmpsDialogRef<DisputeCommitmentComponent, any>;
        signOff?: CmpsDialogRef<ValidateAuthComponent, any>;
        regular?: CmpsDialogRef<CmpsConfirmDialogComponent, any>;
        summary?: CmpsDialogRef<CommitmentSummaryDialog, any>;
    };

    private readonly _linkFocusPromiseCbs: Map<
        number /* observationId */,
        LinkFocusPromiseCallbacks
    >;

    private readonly _savePromiseCbs: Map<number, LinkFocusPromiseCallbacks>;

    /** @private */ get _pending(): boolean {
        return this.state && Object.values(this.state.pending).some((e) => !!e);
    }

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _activatedRoute: ActivatedRoute,
        private _router: Router,
        private readonly _location: Location,
        private readonly _modalService: NgbModal,
        private readonly _ngbDateAdapter: NgbDateAdapter<Date>,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        private readonly _formBuilder: CmpsFormBuilder,
        @Optional() private readonly _formService: CmpsFormService,
        private readonly _authService: AuthService,
        private readonly _snackBar: CmpsSnackBar,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _focusService: BringIntoFocusService,
        private readonly _diffDecoratorService: CoachingDiffDecoratorService,
        private readonly _msalService: MsalService,
    ) {
        this._modalRefs = {};
        this._linkFocusPromiseCbs = new Map();
        this._savePromiseCbs = new Map();
        this._closeGoalValueList = [];
    }

    ngOnInit(): void {
        this._router.onSameUrlNavigation = 'reload'; // TODO revise. careful

        this._form =
            this._formBuilder.create<CommitmentFormFields>(
                CommitmentFormFields,
            );
        Object.entries(this._form.form.controls).forEach(([k, v]) => {
            this._formService?.register([k], v);
        });

        /// ////////

        // TODO move
        this._form.form.controls[this._form.fields.FOCUS.name].valueChanges
            .pipe(
                tap<FocusDto>((value) => {
                    useDispatcher(this, rootSetTag({ focus: value }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // TODO move
        this._form.form.controls[
            this._form.fields.GOALS_COLLECTION.name
        ].valueChanges
            .pipe(
                tap<GoalsCollection>((value) => {
                    /* the goals form might change itself when first loaded (maybe the commitment reducer should do this upfront),
                    so keep the goals form pristine if root form is disabled. or when it's init related */
                    if (this._form.form.disabled || value.initRelated) {
                        this._form.form.controls[
                            this._form.fields.GOALS_COLLECTION.name
                        ].markAsPristine();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // TODO move
        this._formService
            ?.onRegister<FormArray>([CommitmentFormFields.NOTES.name, '*'])
            .pipe(
                tap(() => {
                    this._checkNotes();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // maybe remove duration from the form group
        this._form.form.statusChanges
            .pipe(
                tap(() => {
                    const keys = Object.keys(this._form.form.controls); // should be same as CommitmentFormFields.name

                    const anyButDurationAreDirty = keys
                        .filter((e) => e !== CommitmentFormFields.DURATION.name)
                        .some((e) => this._form.form.controls[e].dirty);
                    const durationIsDirtyAndTouched =
                        this._form.form.controls[
                            CommitmentFormFields.DURATION.name
                        ].dirty &&
                        this._form.form.controls[
                            CommitmentFormFields.DURATION.name
                        ].touched;

                    this._dirty =
                        anyButDurationAreDirty || durationIsDirtyAndTouched;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        const userObs = this._switchboardService.pillarOne.sbSelectedUser();
        if (userObs) {
            userObs
                .pipe(
                    tap((user) => {
                        this._selectedUser = user;
                    }),
                    takeUntilDestroy(this),
                )
                .subscribe();
        }
    }

    ngAfterViewInit(): void {
        this._focusService.registerMutatorSelector(
            '[cmpsCommitmentNext]',
            () => {
                // TODO move. see .container-scroll in scss
                return window.matchMedia(
                    '(min-width: 1200px) and (min-height: 450px)',
                ).matches
                    ? '[cmpsCommitmentNext] .container-scroll'
                    : null;
            },
        );
    }

    ngOnDestroy(): void {
        this._router.onSameUrlNavigation = 'ignore'; // TODO revise. careful

        this._formService?.deleteOnRegister([
            CommitmentFormFields.NOTES.name,
            '*',
        ]);
        Object.keys(this._form.form.controls).forEach((e) => {
            this._formService?.unregister([e]);
        });
        this._switchboardService.commitment.disconnect();
        this._focusService.stopObserving();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    // TODO consolidate?
    canDeactivate(
        component: CommitmentComponent,
        currentRoute: ActivatedRouteSnapshot,
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        return this._askToContinue(currentState, nextState);
    }

    /** @private */
    _onTimerChange(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9493_YOU_ARE_CHANGING_THE_SESSION_TIME_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9425_OK,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._121_CANCEL,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, {
            disableClose: true,
            data,
        });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 1) {
                        // trigger timer to reset to the latest cached time
                        this._form.form.patchValue({
                            duration: '',
                        });
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    stateToProps(prevState?: RootState): void {
        if (!prevState.pending.reload && this.state.pending.reload) {
            this._switchboardService.commitment.disconnect();
        }

        if (prevState.pending.reload && !this.state.pending.reload) {
            this._switchboardService.commitment.connect(this);

            this._deliveryTypeOpts = this.state.coachingEvent.deliveryTypes.map(
                (e) => ({
                    value: e.deliveryTypeId,
                    label: e.deliveryTypeName,
                }),
            );

            this._patchForm();
            this._checkSectionAutoFocus();
            this._checkApprovalAlertInfo();
            this._checkNavigatedFromNotificationOrInsight();
            this._checkReadOnly();
            this._checkReadWrite();
        }

        if (prevState.pending.approval && !this.state.pending.approval) {
            this._patchForm();
            this._checkApprovalAlertInfo();
            this._checkApproval();
        }

        if (
            prevState.pending.reload === this.state.pending.reload &&
            prevState.tagInfo !== this.state.tagInfo
        ) {
            this._form.form.patchValue(
                {
                    [this._form.fields.FOCUS.name]: this.state.tagInfo.focus,
                },
                { emitEvent: false },
            );
        }

        if (prevState.pending.save && !this.state.pending.save) {
            this._lastLinkedObservation = null;

            this._form.form.patchValue(
                {
                    [this._form.fields.DURATION.name]: 0,
                },
                { emitEvent: false },
            );
            this._form.form.markAsPristine();

            let didReplaceState = false;
            if (this._draftId !== this.state.draftId && this.state.draftId) {
                replaceStateToExistingDraftCommitment(
                    this._location,
                    this.state.draftId,
                    undefined,
                    this.state.commitment.commitmentId,
                );
                // replaceState won't trigger paramsWatcher, this needs to be done manually
                this._draftId = this.state.draftId;
                // need to flag that a replaceState happened since pillar-two won't see a route change
                didReplaceState = true;
            } else if (
                this._commitmentId !== this.state.commitment.commitmentId &&
                this.state.commitment.commitmentId != null &&
                !isNaN(this.state.commitment.commitmentId)
            ) {
                replaceStateToExistingCommitment(
                    this._location,
                    this.state.commitment.commitmentId,
                );
                // replaceState won't trigger paramsWatcher, this needs to be done manually
                this._commitmentId = this.state.commitment.commitmentId;
                // need to flag that a replaceState happened since pillar-two won't see a route change
                didReplaceState = true;
            }

            this._checkReloadPillarTwo(didReplaceState, false);
            this._checkApprovalAlertInfo();

            this._savePromiseCbs.forEach((promCbs) => {
                if (promCbs?.resolve != null) {
                    promCbs.resolve();
                }
            });
            this._savePromiseCbs.clear();
        }

        if (prevState.pending.delete && !this.state.pending.delete) {
            /* eslint-disable @typescript-eslint/no-misused-promises */
            (
                this._switchboardService.pillarTwo.sbReload(
                    TabRefId.COMMITMENTS,
                ) || Promise.resolve()
            )
                /* eslint-enable @typescript-eslint/no-misused-promises */
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    if (
                        this.state.draftId &&
                        this.state.commitment.commitmentId
                    ) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        navigateToExistingCommitment(
                            this._router,
                            this._activatedRoute,
                            this.state.commitment.commitmentId,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                        );
                    } else {
                        return navigateToDetail(
                            this._router,
                            this._activatedRoute,
                            true,
                        );
                    }
                })
                .catch(() => null);
        }

        if (prevState.isPublishing !== this.state.isPublishing) {
            if (!this.state.isPublishing || !prevState.isPublishing) {
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._modalRefs.regular && this._modalRefs.regular.close();
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._modalRefs.summary && this._modalRefs.summary.close();
            }

            if (!prevState.isPublishing && this.state.isPublishing) {
                this._publish();
            }
        }

        if (prevState.isClosingReason !== this.state.isClosingReason) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._modalRefs.closeReason && this._modalRefs.closeReason.close();

            if (!prevState.isClosingReason && this.state.isClosingReason) {
                this._closeReason();
            }
        }

        if (prevState.isClosing !== this.state.isClosing) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._modalRefs.close && this._modalRefs.close.close();

            if (!prevState.isClosing && this.state.isClosing) {
                this._close();
            }
        }

        if (prevState.isDisputing !== this.state.isDisputing) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._modalRefs.signOff && this._modalRefs.signOff.close();

            if (!prevState.isDisputing && this.state.isDisputing) {
                this._dispute();
            }
        }

        if (prevState.isSigningOff !== this.state.isSigningOff) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._modalRefs.signOff && this._modalRefs.signOff.close();

            if (!prevState.isSigningOff && this.state.isSigningOff) {
                this._signOff();
            }
        }

        if (
            prevState.lastLinkedObservations !==
                this.state.lastLinkedObservations &&
            this.state.lastLinkedObservations != null
        ) {
            this._linkFocusPromiseCbs.forEach((promCbs, observationId) => {
                if (!this.state.lastLinkedObservations.has(observationId)) {
                    return;
                }

                const obs =
                    this.state.lastLinkedObservations.get(observationId);
                this._linkFocus(this.state.tagInfo.focus, obs, false)
                    .then(() => {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        promCbs && promCbs.resolve && promCbs.resolve();
                        this._linkFocusPromiseCbs.delete(observationId);
                    })
                    .catch(() => {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        promCbs && promCbs.reject && promCbs.reject();
                        this._linkFocusPromiseCbs.delete(observationId);
                    });
            });

            this._lastLinkedObservation = Array.from(
                this.state.lastLinkedObservations.values(),
            ).pop();
        }

        if (
            prevState.isEditing !== this.state.isEditing &&
            prevState.pending.reload === this.state.pending.reload &&
            prevState.pending.save === this.state.pending.save
        ) {
            if (
                !this.state.mirrorCopyAlertInfo ||
                (this.state.mirrorCopyAlertInfo &&
                    this.state.mirrorCopyAlertInfo.isOriginalCommitment !==
                        true)
            ) {
                this._setDisabledState(!this.state.isEditing);
            }
        }
    }

    propsToDispatch(): void {
        this._paramsWatcher()
            .pipe(
                tap(() => {
                    useDispatcher(
                        this,
                        rootReload({
                            performerId: this._userId,
                            draftId: this._draftId,
                            approvalId: this._approvalId,
                            commitmentId: this._commitmentId,
                            coachId: this._authService.user.id,
                            focus: this._focus,
                            observationId: this._observationId,
                            goalMetricIdList: this._goalMetricIdList,
                            edit: this._navigatedWithEditIntent,
                            loadLastApprovalIfItWasDone:
                                this._navigatedFromNotification,
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        overrideCommitment();
    }

    sbHasMatchingFocus(focus: FocusShallow): boolean {
        const value = (this._form.form.value as CommitmentFormValue).focus;
        return (
            focus &&
            value &&
            value.behaviourId != null &&
            focus.behaviourId != null &&
            value.behaviourId === focus.behaviourId &&
            value.subBehaviourId === focus.subBehaviourId
        );
    }

    sbHasMatchingCommitmentId(commitmentId: number): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const cId =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state && this.state.commitment
                ? this.state.commitment.commitmentId
                : this._commitmentId;
        return cId && cId === commitmentId;
    }

    sbHasLinkedFocus(focus: FocusShallow, observationId: number): boolean {
        if (!focus || focus.behaviourId == null || observationId == null) {
            return;
        }

        const value = this._form.form.value as CommitmentFormValue;
        return (
            focus &&
            value &&
            value.observations &&
            value.observations.findIndex((e) => {
                return (
                    e.event &&
                    e.event.eventId === observationId &&
                    e.focusContainers &&
                    e.focusContainers.findIndex((ee) => {
                        return (
                            ee.behaviourId != null &&
                            ee.behaviourId === focus.behaviourId &&
                            ee.subBehaviourId === focus.subBehaviourId
                        );
                    }) !== -1
                );
            }) !== -1
        );
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    sbSetFocus(focus: FocusDto): Promise<void> {
        if (!focus || focus.behaviourId == null) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        }
        useDispatcher(this, rootSetTag({ focus }));
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    sbLinkFocus(focus: FocusShallow, observationId: number): Promise<void> {
        if (!focus || focus.behaviourId == null || observationId == null) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        }

        const value = (this._form.form.value as CommitmentFormValue)
            .observations;
        const next = value ? [...value] : [];

        let observation: ObservationFormValue | ObservationSummaryDto;
        let observationIsInForm: boolean;

        // try to find a matching observation in the form
        // eslint-disable-next-line prefer-const
        observation = next.find((e) => e.event.eventId === observationId);
        if (!observation) {
            // TODO revise. disable for now until a repo like interface is possible for observation summaries
            /* if (this._observationSwitchboardService.observationReview.connected()) {
                const observations = this._observationSwitchboardService.observationReview.observations();
                if (observations) {
                    observation = observations.find(e => e.event && e.event.eventId === observationId);
                }
            } */
            // if observation review isn't present, doesn't have, or doesn't yet have the observation, just go get it
            if (!observation) {
                const promCbs: LinkFocusPromiseCallbacks = {};
                const prom = new Promise<void>((resolve, reject) => {
                    promCbs.resolve = resolve;
                    promCbs.reject = reject;
                });
                this._linkFocusPromiseCbs.set(observationId, promCbs);
                useDispatcher(
                    this,
                    rootRetrieveObservationSummary({ observationId }),
                );
                return prom;
            }
        } else {
            observationIsInForm = true;
        }
        if (!observation) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        }

        return this._linkFocus(focus, observation, observationIsInForm);
    }

    sbUnlinkFocus(focus: FocusShallow, observationId: number): void {
        if (!focus || focus.behaviourId == null || observationId == null) {
            return;
        }

        const value = (this._form.form.value as CommitmentFormValue)
            .observations;
        const next = value ? [...value] : [];

        const observation = next.find((e) => e.event.eventId === observationId);
        if (!observation) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const index =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            observation.focusContainers &&
            observation.focusContainers.findIndex((e) => {
                return (
                    e.behaviourId != null &&
                    e.behaviourId === focus.behaviourId &&
                    e.subBehaviourId === focus.subBehaviourId
                );
            });
        if (index === -1) {
            return;
        }

        const containers = (observation.focusContainers = [
            ...observation.focusContainers,
        ]);
        containers.splice(index, 1);
        if (containers.length === 0) {
            next.splice(index, 1);
        } else {
            next[index].focusContainers = containers;
        }

        this._form.form.patchValue({ observations: next }); // let the component handle the rest
        this._form.form.controls[
            CommitmentFormFields.OBSERVATIONS.name
        ].markAsDirty();
    }

    async sbSave(): Promise<any> {
        if (this._form.form.disabled) {
            // eslint-disable-next-line @typescript-eslint/return-await
            return Promise.resolve(); // no changes can be made, so no need for pop-up
        }

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._11042_DO_YOU_WANT_TO_SAVE_CHANGES_MADE_TO_THIS_COMMITMENT,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        // eslint-disable-next-line @typescript-eslint/return-await
        return ref.result
            .pipe(
                switchMap((ret) => {
                    if (ret !== 0) {
                        // select no, open transcript
                        return Promise.resolve();
                    }
                    this._form.form.updateValueAndValidity();
                    this._form.form.markAsDirty();

                    if (!this._form.form.valid) {
                        this._form.checkWarningsErrors(); // warning should show after YES is selected
                        // eslint-disable-next-line prefer-promise-reject-errors
                        return Promise.reject();
                    }
                    const promCbs: LinkFocusPromiseCallbacks = {};
                    const prom = new Promise<void>((resolve, reject) => {
                        promCbs.resolve = resolve;
                        promCbs.reject = reject;
                    });
                    this._savePromiseCbs.set(this._commitmentId, promCbs);
                    useDispatcher(
                        this,
                        rootSave({
                            formValue: this._form.form.getRawValue(),
                            isGeneric: this._isGeneric,
                            activeMetricId: this._activeMetricId,
                        }),
                    );
                    return from(prom);
                }),
                takeUntilDestroy(this),
            )
            .toPromise();
    }

    sbWillNavigate(): void {
        this._canDeactivateWithoutConfirm = true;
    }

    sbReload(commitmentId: number, draftId: string): void {
        if (this._commitmentId !== commitmentId && this._draftId !== draftId) {
            return;
        }
        useDispatcher(
            this,
            rootReload({
                performerId: this._userId,
                draftId: this._draftId,
                approvalId: this._approvalId,
                commitmentId: this._commitmentId,
                coachId: this._authService.user.id,
                focus: this._focus,
                observationId: this._observationId,
                goalMetricIdList: this._goalMetricIdList,
                edit: this._navigatedWithEditIntent,
                loadLastApprovalIfItWasDone: this._navigatedFromNotification,
            }),
        );
    }

    /** @private */
    _onPublishClick(): void {
        this._formService.markAsDirty();
        this._form.checkWarningsErrors2();
        if (!this._form.form.valid) {
            return;
        }
        const publishAction = rootPublish({ open: true });
        if (!this._form.form.pristine) {
            useDispatcher(
                this,
                rootSave({
                    formValue: this._form.form.getRawValue(),
                    actions: [publishAction],
                    isGeneric: this._isGeneric,
                    activeMetricId: this._activeMetricId,
                }),
            );
        } else {
            useDispatcher(this, publishAction);
        }
    }

    /** @private */
    _onSaveClick(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9500_ARE_YOU_SURE_YOU_WANT_TO_SAVE_THIS_COMMITMENT,
            ClickOptions.SAVECOMMITMENT,
        );
    }

    /** @private */
    _onCloseClick(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9501_ARE_YOU_SURE_YOU_WANT_TO_CLOSE_THIS_COMMITMENT,
            ClickOptions.CLOSECOMMITMENT,
        );
    }

    /** @private */
    _onDeleteClick(): void {
        this.resolveConfirmationModal(
            TranslationKeyMap._9503_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_COMMITMENT,
            ClickOptions.DELETECOMMITMENT,
        );
    }

    /** @private */
    _onEditClick(): void {
        useDispatcher(this, rootEdit({ edit: true }));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    summaryDialogModal(
        dialogData: CommitmentSummaryDialogData,
        nextSendStatus: CoachingEventStatus,
        nextSendScenario: NotificationScenario,
        finalizeCb?: () => void,
    ) {
        const ref = (this._modalRefs.summary = this._dialog.open<
            CommitmentSummaryDialog,
            CommitmentSummaryDialogData,
            CommitmentSummaryDialogResult
        >(CommitmentSummaryDialog, { disableClose: true, data: dialogData }));
        ref.result
            .pipe(
                tap((ret) => {
                    if (!ret) {
                        return;
                    }

                    switch (ret.action) {
                        case CommitmentSummaryDialogResultAction.SEND_FOR_APPROVAL:
                            this._sendApprovalToSignOff(
                                nextSendStatus,
                                nextSendScenario,
                            );
                            break;
                        case CommitmentSummaryDialogResultAction.SEND_SIGN_OFF:
                            if (ret.items.every((e) => e.approved)) {
                                this._sendSignOffApprove(ret.items);
                            } else {
                                this._sendSignOffDispute(ret.items);
                            }
                            break;
                    }
                }),
                finalize(() => {
                    this._modalRefs.summary = null;
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    finalizeCb && finalizeCb();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resolveConfirmationModal(message: string, clickOption: number) {
        const actions =
            clickOption === ClickOptions.MANAGE_GOALS
                ? [
                      {
                          label: this._translateService.instant(
                              TranslationKeyMap._637_OKAY,
                          ),
                          data: 0,
                      },
                  ]
                : [
                      {
                          label: this._translateService.instant(
                              TranslationKeyMap._9297_YES,
                          ),
                          data: 0,
                      },
                      {
                          label: this._translateService.instant(
                              TranslationKeyMap._9298_NO,
                          ),
                          data: 1,
                      },
                  ];

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(message),
            actions,
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    switch (clickOption) {
                        case ClickOptions.DELETECOMMITMENT:
                            // eslint-disable-next-line no-case-declarations
                            let actions: Array<PayloadAction<any, any>>;
                            // disputed (draft) -and- is the draft that was sent for approval
                            if (
                                // also see nextStatus in _sendSignOffDispute()
                                (this.state.commitment.statusId ===
                                    CoachingEventStatus.DRAFT ||
                                    this.state.commitment.statusId ===
                                        CoachingEventStatus.DISPUTED) &&
                                this.state.approval &&
                                this.state.approval.approved === false
                            ) {
                                actions = [
                                    rootSendNotification({
                                        scenario: NotificationScenario.DELETE,
                                        ctx: {
                                            sessionDate:
                                                this.state.event.sessionDate,
                                        },
                                    }),
                                ];
                            }
                            // TODO see if state.draftId can be set to state.approval.draftId when on the approval route
                            useDispatcher(
                                this,
                                rootDelete({
                                    draftId:
                                        this.state.draftId ||
                                        (this.state.approval
                                            ? this.state.approval.draftId
                                            : null),
                                    commitmentId:
                                        this.state.commitment.commitmentId,
                                    actions,
                                }),
                            );
                            break;
                        case ClickOptions.SAVECOMMITMENT:
                            this._form.form.updateValueAndValidity();
                            this._formService.markAsDirty();
                            this._form.checkWarningsErrors2();
                            if (!this._form.form.valid) {
                                return;
                            }

                            useDispatcher(
                                this,
                                rootSave({
                                    formValue: this._form.form.getRawValue(),
                                    isGeneric: this._isGeneric,
                                    activeMetricId: this._activeMetricId,
                                }),
                            );
                            break;
                        case ClickOptions.PUBLISHCOMMITMENT:
                            // Check problem when publish -> Move logic
                            break;
                        case ClickOptions.CLOSECOMMITMENT:
                            useDispatcher(
                                this,
                                rootCloseReason({ open: true }),
                            );
                            break;
                        default:
                            console.log('No valid click option');
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    async _onCallClick(): Promise<void> {
        const token = await this._msalService.getToken(
            this._authService.user.id,
        );
        if (token == null) {
            return;
        }

        const ctx: MeetingContext = {
            commitmentId: this.state?.commitment?.commitmentId,
            behaviourId: this.state?.tagInfo?.behaviour?.id,
            subBehaviourId: this.state?.tagInfo?.subBehaviour?.id,
        };

        await this._switchboardService.transcriptSummary.sbCreateCall({
            subject: 'Compas Teams Meeting', // TODO revise
            participants: [
                {
                    userId: this._authService.user.id,
                    email: this._authService.user.email,
                    isOrganizer: true,
                },
                {
                    userId: this._selectedUser.id,
                    email: this._selectedUser.email,
                },
            ],
            context: JSON.stringify(ctx),
        });
    }

    /** @private */
    async _onChatClick(): Promise<void> {
        const token = await this._msalService.getToken(
            this._authService.user.id,
        );
        if (token == null) {
            return;
        }

        const members = Array.from(
            new Set([
                this._authService.user.id,
                this.state.coach.id,
                this._selectedUser.id,
            ]),
        );

        const behaviour = this.state?.behaviours.find(
            (e) => e.id === this.state?.tagInfo?.behaviour?.id,
        );

        const ctx: ChatContext = {
            commitmentId: this.state?.commitment?.commitmentId,
            behaviourId: this.state?.tagInfo?.behaviour?.id,
            subBehaviourId: this.state?.tagInfo?.subBehaviour?.id,
            coachId:
                this.state.coach.id /* TODO revise for hierarchy changes */,
            performerId: this._selectedUser.id,
        };

        await this._switchboardService.transcriptSummary.sbCreateChat({
            topic: `${this._selectedUser.name ?? ''} - ${
                behaviour?.name ?? ''
            } ${
                this._translateService.instant(
                    TranslationKeyMap._11041_CHAT,
                ) as string
            }`,
            members,
            context: JSON.stringify(ctx),
        });
    }

    /** @private */ _onDismissMessageClick(): void {
        useDispatcher(this, rootDismissMessage({}));
    }

    /** @private */ _onCloseToDetailClick(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigateToDetail(this._router, this._activatedRoute, false);
    }

    /** @private */
    _onFocusClick(
        trigger: TagSetTriggerComponent,
        triggerNext: FocusSelectorSetTriggerComponent,
    ): void {
        const disabled =
            this._form?.form.value?.observations.length > 0 ||
            this.state?.commitment?.statusId !== CoachingEventStatus.DRAFT ||
            !this._form?.form?.enabled;
        if (disabled) {
            return;
        }
        (this.state?.coachingEvent?.newFocusSelectorWithYield
            ? triggerNext
            : trigger
        ).open();
    }

    /** @private */ onGoalCloseClick(option: CommitmentCloseGoalValue): void {
        this._closeGoalValueList.push(option);
    }

    /** @private */
    _onManageGoal(): void {
        const { focus } = this._form.form.value as CommitmentFormValue;

        const hasFocus = focus?.behaviourId != null;

        if (hasFocus) {
            const triggerPayload: FocusSelectorTriggerPayload = {
                triggerSource: FocusSelectorTriggerSource.MANAGE_GOALS,
                commitmentId: this._commitmentId,
            };
            this.triggerNext?.open(triggerPayload);
        } else {
            this.resolveConfirmationModal(
                TranslationKeyMap._11125_A_FOCUS_MUST_BE_SELECTED_BEFORE_SETTING_GOALS,
                ClickOptions.MANAGE_GOALS,
            );
        }
    }

    /** @private */
    _onApproveClick(): void {
        const items = this.state.approval.diff.diffItems.map<ApprovalItem>(
            (e) => ({
                diffItemId: e.diffItemId,
                diffItemContextId: e.diffItemContextId,
                approved: true,
            }),
        );
        this._sendSignOffApprove(items);
    }

    /** @private */
    _onDisputeClick(): void {
        // ignore form changes
        useDispatcher(this, rootDispute({ open: true }));
    }

    /** @private */
    _onViewSummaryClick(): void {
        this.summaryDialogModal(
            {
                summaryView: CommitmentSummaryViewMode.RECEIVER_VIEW,
                state: this.state.approvalAlertInfo.diffItemsInfo.state,
                data: this.state.approvalAlertInfo.diffItemsInfo.items,
                from: this.state.approvalAlertInfo.diffItemsInfo.from,
            },
            null,
            null,
        );
    }

    /** @private */
    _onAcceptAllChangesClick(): void {
        const items = this.state.approval.diff.diffItems.map<ApprovalItem>(
            (e) => ({
                diffItemId: e.diffItemId,
                diffItemContextId: e.diffItemContextId,
                approved: true,
            }),
        );
        this._sendSignOffApprove(items);
    }

    /** @private */
    _onGoToDraftClick(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigateToExistingDraftCommitment(
            this._router,
            this._activatedRoute,
            this.state.commitment.draftAndApproval.draftId,
            undefined,
            this.state.commitment.commitmentId,
            undefined,
            undefined,
            undefined,
            true,
        );
    }

    /** @private */
    _onGoToCommitmentClick(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigateToExistingCommitment(
            this._router,
            this._activatedRoute,
            this.state.commitment.commitmentId,
            undefined,
            undefined,
            undefined,
            false,
        );
    }

    // TODO consider consolidating
    private _paramsWatcher(): Observable<Params> {
        const checkParams = (params: Params): void => {
            this._userId = parseInt(params.userId);
            this._draftId = params.draftId;
            this._approvalId = parseInt(params.approvalId);
            this._commitmentId = parseInt(params.commitmentId);
        };

        const checkQueryParams = (queryParams: Params): void => {
            if (this._userId == null || isNaN(this._userId)) {
                this._userId = parseInt(queryParams.userId);
            }

            this._fromOa =
                queryParams.fromOa === 'true' || queryParams.fromOa === '1';
            this._goalMetricIdList = (queryParams.metricList || '')
                .split('-')
                .filter((x) => x)
                .map((x) => parseInt(x));

            this._focus = reconstructTagFromIdentifierNext(queryParams.focus);
            this._observationId = parseInt(queryParams.observationId);

            const reason = (queryParams.reason || '').toLowerCase();
            this._scrollToGoals = reason === 'goal';
            this._goalId = parseInt(queryParams.goalId);

            this._isGeneric =
                queryParams.isGeneric === 'true' ||
                queryParams.isGeneric === '1';
            this._activeMetricId = isNaN(queryParams.metricId)
                ? null
                : parseInt(queryParams.metricId);

            this._navigatedWithEditIntent =
                queryParams.edit === 'true' || queryParams.edit === '1';
            this._navigatedFromNotification =
                queryParams[NavigationSrcQueryParam] ===
                NavigationSrc.NOTIFICATION;
            this._navigatedFromInsight =
                queryParams.fromInsight === 'true' ||
                queryParams.fromInsight === '1';
        };

        return combineLatest([
            combineLatest([
                combineLatest(
                    this._activatedRoute.pathFromRoot.map((e) => e.params),
                ).pipe(
                    map((arr) =>
                        arr.reduce<Params>((p, c) => ({ ...p, ...c }), {}),
                    ),
                    tap(checkParams),
                    // ensure query params are processed even if they did not change
                    tap(() =>
                        checkQueryParams(
                            this._activatedRoute.snapshot.queryParams,
                        ),
                    ),
                ),
                this._activatedRoute.queryParams.pipe(tap(checkQueryParams)),
            ]),
            this._router.events.pipe(
                filter<NavigationEnd>(
                    (event) => event instanceof NavigationEnd,
                ),
                map((event) => event.url),
                startWith(this._router.url),
                pairwise(),
                // ensure query params are processed even if the url or query params did not change
                tap(() => {
                    const params = this._activatedRoute.snapshot.pathFromRoot
                        .map((e) => e.params)
                        .reduce<Params>((p, c) => ({ ...p, ...c }), {});
                    checkParams(params);
                }),
                tap(() =>
                    checkQueryParams(this._activatedRoute.snapshot.queryParams),
                ),
                startWith([null, this._router.url]),
            ),
        ]);
    }

    private _patchForm(): void {
        // startDate = creation of Commitment
        // activeDate = approval of publish of Commitment, status change to Active
        // closedDate = approval of closure of Commitment, status change to Closed
        const startOrActiveDate = this.state.commitment.activeDate
            ? this.state.commitment.activeDate
            : this.state.commitment.startDate;

        this._form.clearWarningsErrors();

        // using switchboardService'pillar 2 data for hasActiveCmt to set the validator
        const hasActiveCmt =
            this._switchboardService.pillarTwo.sbHasActiveCommitmentWithFocus(
                this.state.commitment?.focus,
            );

        this._form.setValidators(CommitmentFormFields.FOCUS, [
            focusChangeValidator(this.state.commitment),
            focusActiveValidator(hasActiveCmt, this.state.commitment),
        ]);

        this._form.form.reset(
            commitmentFormValueTransformer(
                this.state.event.deliveryTypeId,
                this.state.tagInfo.focus,
                this.state.linkedObservations,
                this.state.comments,
                startOrActiveDate,
                this.state.goalsCollection,
                (this.state.approval ? this.state.approval.draftId : null) ||
                    this.state.commitment.previousDraftId,
            ),
            { emitEvent: false },
        );

        this._setDisabledState(!this.state.isEditing);

        this._loadDatePickerData();
    }

    private _setDisabledState(isDisabled: boolean): void {
        // new, unsaved commitments should always be enabled
        if (this.state.commitment.commitmentId == null) {
            isDisabled = false;
        }

        // commitment status has last say
        if (
            this.state.commitment.statusId ===
                CoachingEventStatus.PRE_APPROVAL ||
            this.state.commitment.statusId === CoachingEventStatus.UPDATING ||
            this.state.commitment.statusId === CoachingEventStatus.PENDING_CLOSE
        ) {
            isDisabled = true;
        }

        isDisabled
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });

        // commitment status has last say
        if (
            !isDisabled &&
            this.state.commitment.statusId !== CoachingEventStatus.DRAFT
        ) {
            this._form.form.controls[CommitmentFormFields.DATE.name].disable({
                emitEvent: false,
            });
            this._checkNotes();
        }
    }

    private _loadDatePickerData(): void {
        // Header date picker min-max dates
        let minDate = new Date();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state.coachingEvent &&
            this.state.coachingEvent.dateRestriction
        ) {
            minDate = subDays(
                new Date(),
                this.state.coachingEvent.dateRestriction.maxPastDays,
            );
        }
        this._minDateStruct = this._ngbDateAdapter.fromModel(minDate);
        this._maxDateStruct = this._ngbDateAdapter.fromModel(
            new Date(Date.now() + 1000),
        );
    }

    // TODO consolidate
    private _askToContinue(
        currentState?: RouterStateSnapshot,
        nextState?: RouterStateSnapshot,
    ): Observable<boolean> | Promise<boolean> | boolean {
        // no modal is open and nothing is pending and the form doesn't have unsaved changes
        const noModal =
            Object.values(this._modalRefs).filter((e) => !!e).length === 0;
        const noPending =
            !this.state ||
            Object.entries(this.state.pending)
                .filter(([key]) => key !== 'config' && key !== 'reload')
                .every(([_, value]) => !value);
        const noUnSaved = !this._dirty;
        const isDeleted =
            this.state &&
            this.state.commitment.statusId === CoachingEventStatus.DELETED;

        if (this._switchboardService.observationReview.connected()) {
            const ret = this._switchboardService.observation.sbCanCancelEdit();
            if (ret instanceof Observable) {
                return ret.pipe(
                    tap((canDeactivate) => {
                        if (canDeactivate) {
                            this._switchboardService.observationReview.sbWillNavigate();
                        }
                    }),
                );
            }
        }

        const canDeactivateWithoutConfirm =
            (noModal && noPending && noUnSaved) ||
            isDeleted ||
            this._canDeactivateWithoutConfirm;
        let changedMetric = false;
        if (currentState && nextState) {
            const cRouteParams = currentState.root.queryParams;
            const nRouteParams = nextState.root.queryParams;
            const cParam = cRouteParams.metricId;
            const nParam = nRouteParams.metricId;
            changedMetric = cParam && nParam && cParam !== nParam;
        }

        // eslint-disable-next-line @typescript-eslint/promise-function-async
        const ask = (message, labelNo, labelYes): Promise<number> => {
            const data: CmpsConfirmDialogOptions = {
                title: this._translateService.instant(
                    TranslationKeyMap._9492_WARNING,
                ),
                message: this._translateService.instant(message),
                actions: [
                    { label: this._translateService.instant(labelNo), data: 1 },
                    {
                        label: this._translateService.instant(labelYes),
                        data: 0,
                    },
                ],
            };
            return this._dialog
                .open<
                    CmpsConfirmDialogComponent,
                    CmpsConfirmDialogOptions,
                    number
                >(CmpsConfirmDialogComponent, { data })
                .result.toPromise();
        };

        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const finalResult = (ret: number) => {
            if (ret === 0) {
                Object.values(this._modalRefs)
                    .filter((e) => !!e)
                    .forEach((e) => {
                        e.close();
                    });
                return true;
            }
            return false;
        };

        if (changedMetric) {
            return ask(
                TranslationKeyMap._9494_YOU_ARE_SELECTING_A_DIFFERENT_METRIC__THIS_WILL_TAKE_YOU_OUTSIDE_THIS_FORM_,
                TranslationKeyMap._121_CANCEL,
                TranslationKeyMap._9425_OK,
            ).then((ret1) => {
                if (ret1 === 0 && !canDeactivateWithoutConfirm) {
                    return ask(
                        TranslationKeyMap._9489_YOU_ARE_LEAVING_THIS_SESSION_,
                        TranslationKeyMap._9491_STAY,
                        TranslationKeyMap._9490_LEAVE,
                    ).then((ret2) => {
                        return finalResult(ret2);
                    });
                }
                return finalResult(ret1);
            });
        }

        if (canDeactivateWithoutConfirm) {
            return true;
        }

        return ask(
            TranslationKeyMap._9489_YOU_ARE_LEAVING_THIS_SESSION_,
            TranslationKeyMap._9491_STAY,
            TranslationKeyMap._9490_LEAVE,
        ).then((ret2) => {
            return finalResult(ret2);
        });
    }

    private _publish(): void {
        const nextSendStatus =
            this.state.commitment.statusId === CoachingEventStatus.DRAFT
                ? CoachingEventStatus.PRE_APPROVAL
                : CoachingEventStatus.UPDATING;
        const nextSendScenario = this.state.commitment.commitmentId
            ? NotificationScenario.PUBLISH
            : NotificationScenario.NEW_COMMITMENT_SEND_APPROVAL;
        const nextSignOffStatus = CoachingEventStatus.ACTIVE;

        this.summaryDialogModal(
            {
                summaryView: CommitmentSummaryViewMode.SENDER_VIEW,
                state: this.state.diffItemsInfo.state,
                data: this.state.diffItemsInfo.items,
                from: this.state.diffItemsInfo.from,
                signOff: () => {
                    useDispatcher(
                        this,
                        rootSignOff({
                            open: true,
                            ctx: nextSignOffStatus,
                        }),
                    );
                },
            },
            nextSendStatus,
            nextSendScenario,
            () => {
                this.state.isPublishing &&
                    useDispatcher(this, rootPublish({ open: false }));
            },
        );
    }

    private _closeReason(): void {
        const ref = (this._modalRefs.closeReason = this._dialog.open<
            CloseCommitmentComponent,
            DispositionReason[],
            CloseContext
        >(CloseCommitmentComponent, {
            data: this.state.coachingEvent.dispositionReasons,
        }));
        ref.result
            .pipe(
                tap((ret) => {
                    if (!ret) {
                        return;
                    }

                    const formValue =
                        this._form.form.getRawValue() as CommitmentFormValue;

                    // TODO maybe find a way for this to be callable from goals.commitment.component
                    if (formValue.goalsCollection) {
                        formValue.goalsCollection.commitmentKpis =
                            pendingCloseAllGoals(
                                formValue.goalsCollection.commitmentKpis,
                            );
                        this._form.form.patchValue({
                            goalsCollection: formValue.goalsCollection,
                        });
                    }

                    useDispatcher(
                        this,
                        rootSave({
                            formValue,
                            actions: [rootClose({ open: true, ctx: ret })],
                            isGeneric: this._isGeneric,
                            activeMetricId: this._activeMetricId,
                        }),
                    );
                }),
                finalize(() => {
                    this._modalRefs.closeReason = null;
                    useDispatcher(this, rootCloseReason({ open: false }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _close(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9492_WARNING,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9531_WOULD_YOU_LIKE_TO_SIGN_OFF_NOW_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9532_SIGN_OFF,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9533_SEND_NOTIFICATION,
                    ),
                    data: 1,
                },
            ],
            hasCloseButton: true,
        };
        const ref = (this._modalRefs.close = this._dialog.open(
            CmpsConfirmDialogComponent,
            { disableClose: true, data },
        ));
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret === 0) {
                        useDispatcher(
                            this,
                            rootSignOff({
                                open: true,
                                ctx: CoachingEventStatus.CLOSED,
                            }),
                        );
                    }

                    if (ret === 1) {
                        this._sendApprovalToSignOff(
                            CoachingEventStatus.PENDING_CLOSE,
                            NotificationScenario.CLOSE,
                        );
                    }
                }),
                finalize(() => {
                    this._modalRefs.close = null;
                    useDispatcher(this, rootClose({ open: false }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _dispute(): void {
        const ref = (this._modalRefs.dispute = this._dialog.open(
            DisputeCommitmentComponent,
            {
                data: this.state.coachingEvent.disputeReasons,
            },
        ));
        ref.result
            .pipe(
                tap((ret) => {
                    if (!ret) {
                        return;
                    }
                    const items =
                        this.state.approval.diff.diffItems.map<ApprovalItem>(
                            (e) => ({
                                diffItemId: e.diffItemId,
                                diffItemContextId: e.diffItemContextId,
                                approved: false,
                            }),
                        );
                    this._sendSignOffDispute(items, null, ret);
                }),
                finalize(() => {
                    useDispatcher(this, rootDispute({ open: false }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _signOff(): void {
        const data: ValidateDialogData = {
            intent: this._translateService.instant(
                TranslationKeyMap._9532_SIGN_OFF,
            ),
            username:
                this._authService.user.id === this.state.performer.id
                    ? this.state.coach.fullName
                    : this.state.performer.fullName,
            intentMessage: this._translateService.instant(
                TranslationKeyMap._9761_PLEASE_LOGIN_TO_VALIDATE_YOUR_DECISION,
            ),
        };
        const ref = (this._modalRefs.signOff = this._dialog.open(
            ValidateAuthComponent,
            { disableClose: true, data },
        ));
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret) {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._modalRefs.summary &&
                            this._modalRefs.summary.close();
                        this._sendApprovalDidSignOff();
                    }
                }),
                finalize(() => {
                    this._modalRefs.signOff = null;
                    useDispatcher(
                        this,
                        rootSignOff({ open: false, ctx: null }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _linkFocus(
        focus: FocusShallow,
        observation: ObservationFormValue | ObservationSummaryDto,
        observationIsInForm: boolean,
    ): Promise<void> {
        if (!focus || !observation) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        }

        const value = (this._form.form.value as CommitmentFormValue)
            .observations;
        const next = value ? [...value] : [];

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const container =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            observation.focusContainers &&
            observation.focusContainers.find((e) => {
                return (
                    e.behaviourId != null &&
                    e.behaviourId === focus.behaviourId &&
                    e.subBehaviourId === focus.subBehaviourId
                );
            });
        if (!container) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject();
        }

        if (observationIsInForm) {
            observation.focusContainers = [
                ...observation.focusContainers,
                container,
            ];
        } else {
            const toAdd = observationFormValueTransformer({
                ...(observation as ObservationSummaryDto),
                focusContainers: [container],
            });
            next.push(toAdd);
        }

        this._form.form.patchValue({ observations: next }); // let the component handle the rest
        this._form.form.controls[
            CommitmentFormFields.OBSERVATIONS.name
        ].markAsDirty();

        return Promise.resolve();
    }

    private _checkSectionAutoFocus(): void {
        if (!this._scrollToGoals) {
            return;
        }

        timer(1000)
            .pipe(
                tap(() => {
                    const el = this._el.nativeElement.querySelector(
                        '[cmpsCommitmentGoals]',
                    );
                    const sel = getScrollParent(el, true);
                    scrollIntoViewInstant(el, sel);
                    this._scrollToGoals = false;
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => of(null)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _checkApprovalAlertInfo(): void {
        this._showApprovalAlert = false;
        this._diffDecoratorService.unDecorate();

        if (!this.state.approvalAlertInfo) {
            return;
        }

        if (this.state.approvalAlertInfo.showAlert) {
            this._showApprovalAlert = true;
        }

        if (this.state.approvalAlertInfo.decorateForm) {
            this._diffDecoratorService.decorate(
                this.state.approvalAlertInfo.diffItemsInfo.items,
                this.state.approvalAlertInfo.diffItemsInfo.state,
            );
        }
    }

    private _checkReloadPillarTwo(
        didReplaceState: boolean,
        onlyTheActiveCommitment: boolean,
    ): void {
        if (!this._switchboardService.pillarTwo.connected()) {
            return;
        }
        const nextCommitmentId = composeCommitmentSummaryItemIdentifier({
            commitmentId: this.state.commitment.commitmentId,
            draftId: !onlyTheActiveCommitment ? this._draftId : null,
        });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._switchboardService.pillarTwo
            .sbReload(TabRefId.COMMITMENTS)
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => {
                if (didReplaceState) {
                    // since replaceStateToExistingCommitment is being used, status needs be set explicitly
                    this._switchboardService.pillarTwo.sbSetActive(
                        TabRefId.COMMITMENTS,
                        nextCommitmentId,
                    );
                } else {
                    return Promise.resolve();
                }
            })
            .then(() => {
                this._switchboardService.pillarTwo.sbScrollIntoView(
                    TabRefId.COMMITMENTS,
                    nextCommitmentId,
                );
            });
    }

    private _checkShowSummaryDialog(): void {
        if (
            !this.state.approvalAlertInfo ||
            !this.state.approvalAlertInfo.showSummaryDialog
        ) {
            return;
        }

        const approvalIsDone = this.state.approval.approved != null;
        const userIsTarget =
            this.state.approval.targetId === this._authService.user.id;
        if (userIsTarget) {
            useDispatcher(this, rootEdit({ edit: true }));
        }

        this.summaryDialogModal(
            {
                summaryView: approvalIsDone
                    ? CommitmentSummaryViewMode.COMMITMENT_UPDATED_RESULT_VIEW
                    : CommitmentSummaryViewMode.RECEIVER_VIEW,
                state: this.state.approvalAlertInfo.diffItemsInfo.state,
                data: this.state.approvalAlertInfo.diffItemsInfo.items,
                from: this.state.approvalAlertInfo.diffItemsInfo.from,
            },
            null,
            null,
        );
    }

    private _checkNavigatedFromNotificationOrInsight(): void {
        if (this._navigatedFromNotification || this._navigatedFromInsight) {
            this._checkReloadPillarTwo(false, false);
            this._checkShowSummaryDialog();
        }
    }

    private _checkApproval(): void {
        if (!this.state.approval) {
            return;
        }

        const approvalIsDone = this.state.approval.approved != null;
        const approved = this.state.approval.approved === true;

        // changes were either approved or disputed and committed, switch to the approval commitment
        replaceStateToExistingDraftCommitment(
            this._location,
            this.state.approval.draftId,
            this.state.approval.approvalId,
            this.state.commitment.commitmentId,
        );

        this._checkReloadPillarTwo(approvalIsDone, approved);
    }

    private _checkReadOnly(): void {
        if (!this.state.isReadOnly) {
            return;
        }

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9701_READ_ONLY,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9788_THIS_COMMITMENT_IS_BEING_DISPUTED__IT_IS_THE_ORIGINAL,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9425_OK,
                    ),
                    data: 1,
                },
            ],
            hasCloseButton: true,
        };
        this._dialog.open(CmpsConfirmDialogComponent, {
            data,
            disableClose: true,
        });
    }

    private _checkReadWrite(): void {
        if (!this.state.isReadWrite) {
            return;
        }

        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9789_READ___WRITE,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9790_THIS_COMMITMENT_IS_BEING_DISPUTED__THIS_IS_THE_VERSION,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9425_OK,
                    ),
                    data: 1,
                },
            ],
            hasCloseButton: true,
        };
        this._dialog.open(CmpsConfirmDialogComponent, {
            data,
            disableClose: true,
        });
    }

    private _sendApprovalDidSignOff(): void {
        const { to } = determineNotificationAddress(this._authService.user.id, [
            this.state.performer,
            this.state.coach,
        ]);

        let nextScenario: NotificationScenario;
        if (this.state.signingOffCtx === CoachingEventStatus.ACTIVE) {
            if (
                this.state.commitment.statusId === CoachingEventStatus.DRAFT ||
                this.state.commitment.statusId ===
                    CoachingEventStatus.PRE_APPROVAL
            ) {
                nextScenario = NotificationScenario.SIGN_OFF;
            }
            if (
                this.state.commitment.statusId === CoachingEventStatus.ACTIVE ||
                this.state.commitment.statusId === CoachingEventStatus.UPDATING
            ) {
                nextScenario =
                    NotificationScenario.COMMITMENT_UPDATE_DID_SIGN_OFF;
            }
        }
        if (this.state.signingOffCtx === CoachingEventStatus.CLOSED) {
            nextScenario = NotificationScenario.COMMITMENT_CLOSE_DID_SIGN_OFF;
        }

        const action = rootSendNotification({
            scenario: nextScenario,
            ctx: {
                behaviourId: this.state.commitment.behaviour,
                subBehaviourId: this.state.commitment.subBehaviour,
                sessionDate: this.state.event.sessionDate,
            },
        });

        useDispatcher(
            this,
            sendApproval({
                toUserId: to.id,
                nextStatus: this.state.signingOffCtx,
                approved: true,
                actions: [action],
            }),
        );

        // TODO move to after api call success
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data: {
                text: this._translateService.instant(
                    TranslationKeyMap._9769_THE_COMMITMENT_FOR___FULLNAME___HAS_BEEN_SIGNED_OFF_SUCCESSFULLY,
                    { fullName: to.fullName },
                ),
            } as CmpsTextOnlySnackBarOptions,
        });
    }

    private _sendApprovalToSignOff(
        nextStatus: CoachingEventStatus,
        nextScenario: NotificationScenario,
    ): void {
        const { to } = determineNotificationAddress(this._authService.user.id, [
            this.state.performer,
            this.state.coach,
        ]);

        let nextScenarioCtx: CloseContext;
        let closeReason = {} as any as CloseContext;
        if (nextScenario === NotificationScenario.CLOSE) {
            nextScenarioCtx = this.state.closingCtx;
            closeReason = this.state.closingCtx;
        }

        const action = rootSendNotification({
            scenario: nextScenario,
            ctx: {
                ...(nextScenarioCtx || {}),
                behaviourId: this.state.commitment.behaviour,
                subBehaviourId: this.state.commitment.subBehaviour,
            },
        });

        useDispatcher(
            this,
            sendApproval({
                toUserId: to.id,
                nextStatus,
                approved: null,
                closeReasonId: closeReason.dispositionReasonId,
                closeReasonName: closeReason.reason,
                actions: [action],
            }),
        );

        // TODO move to after api call success
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data: {
                text: this._translateService.instant(
                    TranslationKeyMap._9770_THE_COMMITMENT_FOR___FULLNAME___HAS_BEEN_SENT_FOR_APPROVAL,
                    { fullName: this.state.performer.fullName },
                ),
            } as CmpsTextOnlySnackBarOptions,
        });
    }

    private _sendSignOffApprove(items: ApprovalItem[]): void {
        let nextStatus: CoachingEventStatus;
        let nextScenario: NotificationScenario;
        let nextStatusKey: string;
        if (
            this.state.commitment.statusId === CoachingEventStatus.PRE_APPROVAL
        ) {
            nextStatus = CoachingEventStatus.ACTIVE;
            nextScenario = NotificationScenario.APPROVE;
            nextStatusKey = TranslationKeyMap._125_ACTIVE;
        }
        if (this.state.commitment.statusId === CoachingEventStatus.UPDATING) {
            nextStatus = CoachingEventStatus.ACTIVE;
            nextScenario = NotificationScenario.COMMITMENT_APPROVE;
            nextStatusKey = TranslationKeyMap._125_ACTIVE;
        }
        if (
            this.state.commitment.statusId === CoachingEventStatus.PENDING_CLOSE
        ) {
            nextStatus = CoachingEventStatus.CLOSED;
            nextScenario = NotificationScenario.COMMITMENT_CLOSE_DID_APPROVE;
            nextStatusKey = TranslationKeyMap._7440_CLOSED;
        }

        const action = rootSendNotification({
            scenario: nextScenario,
            ctx: {
                behaviourId: this.state.commitment.behaviour,
                subBehaviourId: this.state.commitment.subBehaviour,
            },
        });

        useDispatcher(
            this,
            rootSignOffApproval({
                nextStatus,
                approved: true,
                items,
                actions: [action],
            }),
        );

        // TODO move to after api call success
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data: {
                text: this._translateService.instant(
                    TranslationKeyMap._9771_THE_COMMITMENT_HAS_BEEN_SIGNED_OFF_SUCCESSFULLY_AND_IT_IS___STATUS___NOW,
                    {
                        status: (
                            this._translateService.instant(nextStatusKey) || ''
                        ).toLowerCase(),
                    },
                ),
            } as CmpsTextOnlySnackBarOptions,
        });
    }

    private _sendSignOffDispute(
        items: ApprovalItem[],
        nextScenarioCtx?: any,
        closeContext?: CloseContext,
    ): void {
        let nextStatus: CoachingEventStatus;
        let nextScenario: NotificationScenario;
        if (
            this.state.commitment.statusId === CoachingEventStatus.PRE_APPROVAL
        ) {
            // nextStatus = CoachingEventStatus.DISPUTED; // TODO revise. switch to this
            nextStatus = CoachingEventStatus.DRAFT; // TODO revise. remove
            nextScenario = NotificationScenario.COMMITMENT_DISPUTE;
        }
        if (
            this.state.commitment.statusId === CoachingEventStatus.PENDING_CLOSE
        ) {
            nextStatus = CoachingEventStatus.DISPUTED; // TODO revise
            // nextStatus = CoachingEventStatus.DRAFT; // TODO revise. remove
            nextScenario = NotificationScenario.DISPUTE;
            nextScenarioCtx = {
                ...(nextScenarioCtx || {}),
                reason: closeContext.reason,
                comment: closeContext.comment,
            };
        }
        if (this.state.commitment.statusId === CoachingEventStatus.UPDATING) {
            nextStatus = CoachingEventStatus.DISPUTED; // TODO revise
            // nextStatus = CoachingEventStatus.DRAFT; // TODO revise. remove
            nextScenario = this.state.commitment.commitmentId
                ? NotificationScenario.UPDATE_COMMITMENT_DISPUTE
                : NotificationScenario.COMMITMENT_DISPUTE;
        }

        const action = rootSendNotification({
            scenario: nextScenario,
            ctx: {
                ...(nextScenarioCtx || {}),
                behaviourId: this.state.commitment.behaviour,
                subBehaviourId: this.state.commitment.subBehaviour,
            },
        });

        useDispatcher(
            this,
            rootSignOffApproval({
                nextStatus,
                approved: false,
                items,
                actions: [action],
            }),
        );

        // TODO move to after api call success
        this._snackBar.open(CmpsTextOnlySnackBarComponent, {
            duration: 5000,
            placement: 'bottom-right',
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            data: {
                text: this._translateService.instant(
                    TranslationKeyMap._9772_THE_COMMITMENT_HAS_BEEN_SIGNED_OFF_AND_IT_IS_DRAFT_NOW,
                ),
            } as CmpsTextOnlySnackBarOptions,
        });
    }

    private _checkNotes(): void {
        // TODO revise iWill/iWillKnow for custom default commitment notes
        const cats = [NoteCategory.I_WILL, NoteCategory.I_WILL_KNOW];
        const notes = this._formService?.get<FormArray>([
            CommitmentFormFields.NOTES.name,
        ]);
        notes?.controls?.forEach((e, ei) => {
            const val = e.value as NoteDetailFormValue;
            if (cats.includes(val.category)) {
                const note = this._formService?.get<FormGroup>([
                    CommitmentFormFields.NOTES.name,
                    String(ei),
                ]);
                note?.disable({ emitEvent: false });
            }
        });
    }
}
